import { useEffect, useMemo, useState } from "react";
import { KeyRound, Plus, Save, ShieldCheck, Smartphone, Trash2, UserRoundCog, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const ACTIONS = [
  ["rapportino","Rapportini"],
  ["ddt","DDT"],
  ["fattura","Fatture"],
  ["presenze","Presenze"],
  ["task","Task"],
  ["documenti","Documenti"],
];

const ROLES = {
  responsabile_cantiere: { label:"Responsabile cantiere", actions:["ddt","fattura"] },
  site_engineer: { label:"Site Engineer", actions:["rapportino","presenze"] },
  admin: { label:"Amministratore Field", actions:["rapportino","ddt","fattura","presenze","task","documenti"] },
};

const EMPTY = {
  name:"",
  active:true,
  field_role:"site_engineer",
  field_actions:["rapportino","presenze"],
  all_sites:false,
  site_ids:[],
  code:"",
};

export default function FieldAccessSettings({ sites=[] }) {
  const [rows,setRows]=useState([]);
  const [editing,setEditing]=useState(null);
  const [busy,setBusy]=useState(false);

  const load=async()=>{
    try { setRows(await api.fieldProfiles()); }
    catch(e){ toast.error(e?.response?.data?.detail || "Impossibile caricare gli accessi Field"); }
  };

  useEffect(()=>{ load(); },[]);

  const activeSites=useMemo(()=>sites.filter(s=>s.status!=="chiuso"),[sites]);

  const setRole=(role)=>{
    const preset=ROLES[role];
    setEditing(e=>({
      ...e,
      field_role:role,
      field_actions:[...(preset?.actions||[])],
      all_sites:role==="admin" ? true : e.all_sites,
      site_ids:role==="admin" ? [] : e.site_ids,
    }));
  };

  const toggleAction=(key)=>setEditing(e=>({
    ...e,
    field_actions:(e.field_actions||[]).includes(key)
      ? e.field_actions.filter(x=>x!==key)
      : [...(e.field_actions||[]),key],
  }));

  const toggleSite=(id)=>setEditing(e=>({
    ...e,
    site_ids:(e.site_ids||[]).includes(id)
      ? e.site_ids.filter(x=>x!==id)
      : [...(e.site_ids||[]),id],
  }));

  const save=async()=>{
    if(!editing?.name?.trim()){toast.error("Inserisci il nome");return;}
    if(!editing.id && !/^\d{4,12}$/.test(editing.code||"")){toast.error("Imposta un codice da 4 a 12 cifre");return;}
    if(editing.code && !/^\d{4,12}$/.test(editing.code)){toast.error("Il codice deve contenere da 4 a 12 cifre");return;}
    setBusy(true);
    try{
      const payload={
        name:editing.name.trim(),
        active:!!editing.active,
        field_role:editing.field_role,
        field_actions:editing.field_actions||[],
        all_sites:editing.field_role==="admin" ? true : !!editing.all_sites,
        site_ids:(editing.field_role==="admin" || editing.all_sites) ? [] : (editing.site_ids||[]),
        ...(editing.code?.trim()?{code:editing.code.trim()}:{}),
      };
      if(editing.id) await api.updateFieldProfile(editing.id,payload);
      else await api.createFieldProfile(payload);
      toast.success(editing.id?"Accesso Field aggiornato":"Accesso Field creato");
      setEditing(null);
      await load();
    }catch(e){toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");}
    finally{setBusy(false);}
  };

  const disable=async(row)=>{
    if(!window.confirm("Disattivare l'accesso Field di "+row.name+"?"))return;
    try{await api.disableFieldProfile(row.id);toast.success("Accesso Field disattivato");load();}
    catch(e){toast.error(e?.response?.data?.detail || "Operazione non riuscita");}
  };

  return <div className="space-y-5">
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-orange-600"/>
            <h2 className="font-display font-bold text-xl text-slate-900">Permessi Ellebierre Field</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1">Accessi indipendenti dal login backend. Ogni profilo Field usa solo codice personale, ruolo, permessi e cantieri assegnati.</p>
        </div>
        <button onClick={()=>setEditing({...EMPTY})} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold">
          <Plus className="w-4 h-4"/>Nuovo accesso Field
        </button>
      </div>
    </div>

    <div className="grid lg:grid-cols-2 gap-4">
      {rows.map(row=><div key={row.id} className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <div className={"w-12 h-12 rounded-2xl flex items-center justify-center "+(row.active?"bg-emerald-50 text-emerald-700":"bg-slate-100 text-slate-400")}><UserRoundCog className="w-6 h-6"/></div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-900 truncate">{row.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{ROLES[row.field_role]?.label || row.field_role || "Ruolo non impostato"}</div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(row.field_actions||[]).map(k=><span key={k} className="text-[11px] px-2 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-100">{ACTIONS.find(x=>x[0]===k)?.[1]||k}</span>)}
            </div>
            <div className="text-xs text-slate-500 mt-3">
              {row.all_sites ? "Tutti i cantieri" : ((row.site_ids||[]).length+" cantieri assegnati")}
              {" · "}{row.code_configured ? "Codice configurato" : "Codice mancante"}
            </div>
            {row.last_login && <div className="text-[11px] text-slate-400 mt-1">Ultimo accesso: {new Date(row.last_login).toLocaleString("it-IT")}</div>}
          </div>
          <div className="flex gap-1">
            <button onClick={()=>setEditing({...row,code:""})} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Modifica"><ShieldCheck className="w-4 h-4"/></button>
            {row.active && <button onClick={()=>disable(row)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Disattiva"><Trash2 className="w-4 h-4"/></button>}
          </div>
        </div>
      </div>)}
      {!rows.length && <div className="lg:col-span-2 bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-slate-500">Nessun accesso Field configurato.</div>}
    </div>

    {editing && <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[94vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center">
          <div>
            <div className="font-display font-bold text-slate-900">{editing.id?"Modifica accesso Field":"Nuovo accesso Field"}</div>
            <div className="text-xs text-slate-500">Nessun account Google o login backend necessario.</div>
          </div>
          <button onClick={()=>setEditing(null)} className="ml-auto p-2 text-slate-400 hover:text-slate-700"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="text-sm">
              <span className="block font-medium text-slate-700 mb-1">Nome utente Field</span>
              <input value={editing.name||""} onChange={e=>setEditing({...editing,name:e.target.value})} className="w-full border border-slate-200 rounded-xl px-3 py-3" placeholder="Nome e cognome"/>
            </label>
            <label className="text-sm">
              <span className="block font-medium text-slate-700 mb-1">Codice personale</span>
              <div className="relative">
                <KeyRound className="absolute left-3 top-3.5 w-4 h-4 text-slate-400"/>
                <input inputMode="numeric" value={editing.code||""} onChange={e=>setEditing({...editing,code:e.target.value.replace(/\D/g,"").slice(0,12)})}
                  className="w-full border border-slate-200 rounded-xl pl-10 pr-3 py-3 font-mono" placeholder={editing.code_configured?"Lascia vuoto per mantenerlo":"4–12 cifre"}/>
              </div>
            </label>
          </div>

          <div>
            <div className="font-semibold text-sm text-slate-800 mb-2">Ruolo Field</div>
            <div className="grid sm:grid-cols-3 gap-2">
              {Object.entries(ROLES).map(([key,v])=><button type="button" key={key} onClick={()=>setRole(key)} className={"px-3 py-3 rounded-xl border text-sm font-semibold "+(editing.field_role===key?"border-orange-500 bg-orange-50 text-orange-700":"border-slate-200 text-slate-600")}>{v.label}</button>)}
            </div>
          </div>

          <div>
            <div className="font-semibold text-sm text-slate-800 mb-2">Permessi pagina Field</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {ACTIONS.map(([key,label])=><label key={key} className={"flex items-center gap-3 border-2 rounded-xl p-3 "+((editing.field_actions||[]).includes(key)?"border-orange-400 bg-orange-50":"border-slate-200")}>
                <input type="checkbox" className="w-5 h-5 accent-orange-600" checked={(editing.field_actions||[]).includes(key)} onChange={()=>toggleAction(key)}/>
                <span className="font-semibold text-sm">{label}</span>
              </label>)}
            </div>
          </div>

          {editing.field_role!=="admin" && <div>
            <label className="inline-flex items-center gap-2 text-sm font-semibold mb-3">
              <input type="checkbox" checked={!!editing.all_sites} onChange={e=>setEditing({...editing,all_sites:e.target.checked,site_ids:e.target.checked?[]:editing.site_ids})}/>
              Accesso a tutti i cantieri
            </label>
            {!editing.all_sites && <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {activeSites.map(s=><label key={s.id} className="flex items-center gap-3 px-3 py-3 hover:bg-slate-50">
                <input type="checkbox" className="w-5 h-5 accent-orange-600" checked={(editing.site_ids||[]).includes(s.id)} onChange={()=>toggleSite(s.id)}/>
                <div><div className="font-medium text-sm">{s.name}</div>{s.address&&<div className="text-xs text-slate-400">{s.address}</div>}</div>
              </label>)}
            </div>}
          </div>}

          <label className="inline-flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={!!editing.active} onChange={e=>setEditing({...editing,active:e.target.checked})}/>
            Accesso Field attivo
          </label>

          <button onClick={save} disabled={busy} className="w-full py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4"/>{busy?"Salvataggio…":"Salva accesso Field"}
          </button>
        </div>
      </div>
    </div>}
  </div>;
}
